import { useState, useEffect } from "react";
import { Star, ShoppingCart, Eye } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/hooks/useCartSync";
import { useLanguage } from "@/hooks/useLanguage";
import { useNavigate } from "react-router-dom";
import { formatPriceWithCurrency } from "@/utils/currency";
import { useCurrency } from "@/hooks/useCurrency";
import { useHaptics } from "@/hooks/useHaptics";
import { useProductTranslation } from "@/hooks/useProductTranslation";
import { DealBadge } from "@/components/DealBadge";
import { useProductDeal } from "@/hooks/useProductDeal";
import { ProductRating } from "./ProductRating";
import { useCategoryTranslation } from "@/hooks/useCategoryTranslation";

interface Product {
  id: string;
  title: string;
  price_cents: number;
  currency: string;
  images: string[] | null;
  vendor_id: string;
  stock: number;
  active: boolean;
  description: string | null;
  category: string | null;
  brand: string | null;
  average_rating?: number;
  review_count?: number;
  vendor?: {
    id: string;
    full_name: string;
    business_name: string | null;
  };
}

const products = [
  {
    id: 1,
    name: "iPhone 14 Pro Max",
    price: 1200,
    originalPrice: 1350,
    rating: 4.8,
    reviews: 124,
    image: "https://images.unsplash.com/photo-1601593346740-925612772716?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80",
    badge: "Populaire",
    seller: "TechKin Store"
  },
  {
    id: 2,
    name: "Samsung Galaxy S24",
    price: 950,
    originalPrice: null,
    rating: 4.6,
    reviews: 89,
    image: "https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80",
    badge: "Nouveau",
    seller: "Mobile Plus"
  },
  {
    id: 3,
    name: "MacBook Air M2",
    price: 1100,
    originalPrice: 1299,
    rating: 4.9,
    reviews: 156,
    image: "https://images.unsplash.com/photo-1541807084-5c52b6b3adef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80",
    badge: "Promo",
    seller: "AppleKin"
  },
  {
    id: 4,
    name: "Nike Air Max 270",
    price: 120,
    originalPrice: 150,
    rating: 4.5,
    reviews: 203,
    image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80",
    badge: "Sport",
    seller: "SneakerZone"
  },
  {
    id: 5,
    name: "Sony WH-1000XM5",
    price: 350,
    originalPrice: 400,
    rating: 4.7,
    reviews: 98,
    image: "https://images.unsplash.com/photo-1583394838336-acd977736f90?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80",
    badge: "Audio",
    seller: "SoundTech"
  },
  {
    id: 6,
    name: "Canon EOS R6",
    price: 2200,
    originalPrice: 2500,
    rating: 4.8,
    reviews: 67,
    image: "https://images.unsplash.com/photo-1606983340126-99ab4feaa64a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80",
    badge: "Photo",
    seller: "PhotoPro"
  }
];

const ProductCard = ({ product }: { product: Product }) => {
  const { addItem } = useCart();
  const { displayCurrency, convertPrice } = useCurrency();
  const navigate = useNavigate();
  const { impact, notification } = useHaptics();
  const { deal, calculateDiscountedPrice } = useProductDeal(product.id, product.category || undefined);
  const { t } = useLanguage();
  
  // Traduction du produit
  const { title, description } = useProductTranslation(
    product.id,
    product.title,
    product.description || ''
  );

  // Traduction de la catégorie
  const { categoryName } = useCategoryTranslation(product.category);

  const handleAddToCart = async () => {
    await impact('medium');
    addItem({
      id: product.id,
      title: product.title,
      price_cents: product.price_cents,
      currency: product.currency,
      images: product.images || [],
      vendor_id: product.vendor_id,
      vendor_name: product.vendor?.business_name || product.vendor?.full_name || 'Vendeur'
    });
    await notification('success');
  };

  const handleProductClick = () => {
    navigate(`/product/${product.id}`);
  };

  return (
    <Card 
      className="group cursor-pointer overflow-hidden hover:shadow-elegant transition-all duration-300 hover:-translate-y-1 bg-gradient-card border-0" 
      onClick={handleProductClick}
    >
      <div className="relative aspect-square overflow-hidden">
        <img
          src={product.images?.[0] || '/placeholder.svg'}
          alt={title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3">
          <Badge className="bg-gradient-primary">
            {categoryName || t('product.category')}
          </Badge>
        </div>
        <div className="absolute top-3 right-3">
          <DealBadge productId={product.id} />
        </div>
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button size="sm" variant="secondary" className="h-8 w-8 p-0">
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <CardContent className="p-4">
        <div className="space-y-3">
          <div>
            <h3 className="font-semibold text-lg group-hover:text-primary transition-colors line-clamp-1">
              {title}
            </h3>
            <ProductRating 
              rating={product.average_rating || 0}
              reviewCount={product.review_count || 0}
              size="sm"
              className="mt-1"
            />
            {product.brand && (
              <p className="text-xs text-muted-foreground">
                {product.brand}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              {t('product.by')} {product.vendor?.business_name || product.vendor?.full_name || t('product.seller')}
            </p>
            {product.stock <= 5 && (
              <p className="text-xs text-muted-foreground">
                {t('product.stock')}: {product.stock}
              </p>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              {deal ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-bold text-destructive">
                      {formatPriceWithCurrency(
                        calculateDiscountedPrice(product.price_cents),
                        product.currency,
                        displayCurrency,
                        convertPrice
                      )}
                    </span>
                    <Badge variant="destructive" className="text-xs">
                      -{deal.discount_percentage}%
                    </Badge>
                  </div>
                  <span className="text-sm text-muted-foreground line-through">
                    {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                  </span>
                </>
              ) : (
                <span className="text-xl font-bold text-primary">
                  {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                </span>
              )}
            </div>
            <Button 
              size="sm" 
              className="bg-gradient-primary hover:shadow-glow"
              onClick={(e) => {
                e.stopPropagation();
                handleAddToCart();
              }}
              disabled={product.stock <= 0}
            >
              <ShoppingCart className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ProductGrid = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          vendor:profiles!vendor_id(id, full_name, business_name, vendor_status)
        `)
        .eq('active', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      
      // Afficher les produits des vendeurs approuvés OU sans statut (pour rétrocompatibilité)
      const filteredData = (data || []).filter(
        (product) => !product.vendor || 
                     product.vendor.vendor_status === 'approved' || 
                     product.vendor.vendor_status === null
      );
      
      // Fetch ratings for each product
      const productsWithRatings = await Promise.all(
        filteredData.map(async (product) => {
          try {
            const [avgResult, countResult] = await Promise.all([
              supabase.rpc('get_product_average_rating', { product_uuid: product.id }),
              supabase.rpc('get_product_review_count', { product_uuid: product.id })
            ]);
            
            return {
              ...product,
              average_rating: avgResult.data || 0,
              review_count: countResult.data || 0
            };
          } catch (err) {
            console.error(`Error fetching ratings for product ${product.id}:`, err);
            return {
              ...product,
              average_rating: 0,
              review_count: 0
            };
          }
        })
      );
      
      setProducts(productsWithRatings);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAllProducts = () => {
    navigate('/products');
  };

  if (loading) {
    return (
      <section className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('products.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('products.description')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-square bg-muted"></div>
                <CardContent className="p-4 space-y-3">
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-3 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }
  return (
    <section id="products-section" className="py-16">
      <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('products.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('products.description')}
            </p>
          </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        
        {products.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {t('products.no_products')}
              </p>
            </div>
        )}
        
        <div className="text-center mt-12">
          <Button size="lg" variant="outline" onClick={handleViewAllProducts}>
            {t('products.view_all')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProductGrid;